/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore';
import { Button } from '@cobliteam/ui/components';
import { InputSearch } from '@cobliteam/ui/form';
import Icons from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import GlobalSearchContent from './containers/GlobalSearchContent';
import useFetchData from './hooks/useFetchData';
import { GlobalSearchProvider } from './provider/GlobalSearchProvider';
import { ProviderPersistQueryClient } from './provider/PersisterProvider';

const INPUT_SEARCH_CLASS = 'w-[300px]';

const GlobalSearchFallback = () => {
  return (
    <InputSearch onChange={() => ''} size='sm' className={cn(INPUT_SEARCH_CLASS, '!border-warning-6 !bg-warning-2')} placeholder='Erro ao carregar a busca global...' disabled />
  );
};

const GlobalSearchLoading = () => {
  return <InputSearch onChange={() => ''} loading size='sm' className={cn(INPUT_SEARCH_CLASS)} placeholder='Carregando a busca global...' disabled />;
};

const GlobalSearchError = () => {
  return (
    <div className='flex items-center justify-center'>
      <Button variant='surface' size='sm' color='danger' icon={<Icons name='x' />} />
    </div>
  );
};

const GlobalSearchTrigger = () => {
  const location = useLocation().pathname;
  const vehicleGroupIds = useGlobalFilterStore((state: any) => state.vehicleGroupIds);
  const { sidebar } = useDashboard();
  const { isLoading, fuse, data, isRefetching, error, handleUpdateData } = useFetchData({ currentLocation: location, vehicleGroupIds, pagesFlatted: sidebar.pagesFlatted });

  useEffect(() => {
    if (vehicleGroupIds.length > 0) handleUpdateData?.();
  }, [vehicleGroupIds]);

  if (isLoading) return <GlobalSearchLoading />;
  if (!(fuse && data)) return <GlobalSearchFallback />;
  if (error) return <GlobalSearchError />;

  return (
    <GlobalSearchProvider fuse={fuse} data={data}>
      <GlobalSearchContent handleUpdateData={handleUpdateData} isRefetching={isRefetching} />
    </GlobalSearchProvider>
  );
};

const GlobalSearch = () => {
  const { runtimeFlags } = useDashboard();
  const hasValidGroupOrVehicles = !(runtimeFlags.noValidGroupOrVehicles ?? false);
  if (!hasValidGroupOrVehicles) return null;

  return (
    <ErrorBoundary
      fallback={<GlobalSearchError />}
      onError={(error, info) => {
        DatadogTrack.error(error, { component: 'GlobalSearch', info });
      }}
    >
      <Suspense fallback={<GlobalSearchLoading />}>
        <ProviderPersistQueryClient>
          <GlobalSearchTrigger />
        </ProviderPersistQueryClient>
      </Suspense>
    </ErrorBoundary>
  );
};

export default GlobalSearch;
